import React, { useState } from 'react'
import { Input, InputNumber, Select, Button, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { refineInfographic } from '../../../../api/assessmentReview'
import RefinementReview from '../shared/RefinementReview'
import ListEditor from '../shared/ListEditor'
import styles from '../shared/EditorCommon.module.css'

const { TextArea } = Input

const trendOptions = [
  { value: 'improving', label: 'Improving' },
  { value: 'stable', label: 'Stable' },
  { value: 'declining', label: 'Declining' }
]

const priorityOptions = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
]

const ExecutiveSummaryEditor = ({ data, onChange, onSave, onCancel, saving, clientId, assessmentId }) => {
  const [formData, setFormData] = useState(data || {})
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [showRefinedReview, setShowRefinedReview] = useState(false)
  const [refinedData, setRefinedData] = useState(null)
  const [originalData, setOriginalData] = useState(null)

  const updateFormData = (newData) => {
    setFormData(newData)
    if (onChange) {
      onChange(newData)
    }
  }

  const handleOverallChange = (field, value) => {
    const newData = {
      ...formData,
      overall_assessment: { ...formData.overall_assessment, [field]: value }
    }
    updateFormData(newData)
  }

  const handleOverallQuickActionsChange = (index, value) => {
    const actions = [...(formData.overall_assessment.quick_actions || [])]
    actions[index] = value
    const newData = {
      ...formData,
      overall_assessment: { ...formData.overall_assessment, quick_actions: actions }
    }
    updateFormData(newData)
  }

  const addOverallQuickAction = () => {
    const actions = [...(formData.overall_assessment.quick_actions || [])]
    actions.push('')
    const newData = {
      ...formData,
      overall_assessment: { ...formData.overall_assessment, quick_actions: actions }
    }
    updateFormData(newData)
  }

  const removeOverallQuickAction = (index) => {
    const actions = formData.overall_assessment.quick_actions.filter((_, i) => i !== index)
    const newData = {
      ...formData,
      overall_assessment: { ...formData.overall_assessment, quick_actions: actions }
    }
    updateFormData(newData)
  }

  const handleMetricChange = (index, field, value) => {
    const metrics = [...(formData.key_metrics_to_track || [])]
    if (metrics[index]) {
      metrics[index] = { ...metrics[index], [field]: value }
      updateFormData({ ...formData, key_metrics_to_track: metrics })
    }
  }

  const addMetric = () => {
    const metrics = [...(formData.key_metrics_to_track || [])]
    metrics.push({
      name: 'New Metric',
      current_value: 0,
      target_value: 100,
      unit: '',
      direction: 'up',
      progress_pct: 0
    })
    updateFormData({ ...formData, key_metrics_to_track: metrics })
  }

  const removeMetric = (index) => {
    const metrics = formData.key_metrics_to_track.filter((_, i) => i !== index)
    updateFormData({ ...formData, key_metrics_to_track: metrics })
  }

  const handleWeeklyChange = (field, value) => {
    const newData = {
      ...formData,
      weekly_structure: { ...formData.weekly_structure, [field]: value }
    }
    updateFormData(newData)
  }

  const handleWeeklyCurrentChange = (field, value) => {
    const newData = {
      ...formData,
      weekly_structure: {
        ...formData.weekly_structure,
        current: { ...formData.weekly_structure.current, [field]: value }
      }
    }
    updateFormData(newData)
  }

  const handleWeeklyTargetChange = (field, value) => {
    const newData = {
      ...formData,
      weekly_structure: {
        ...formData.weekly_structure,
        target: { ...formData.weekly_structure.target, [field]: value }
      }
    }
    updateFormData(newData)
  }

  const handleWeeklyActionsChange = (index, value) => {
    const actions = [...(formData.weekly_structure.what_to_do || [])]
    actions[index] = value
    handleWeeklyChange('what_to_do', actions)
  }

  const addWeeklyAction = () => {
    handleWeeklyChange('what_to_do', [...(formData.weekly_structure.what_to_do || []), ''])
  }

  const removeWeeklyAction = (index) => {
    handleWeeklyChange('what_to_do', formData.weekly_structure.what_to_do.filter((_, i) => i !== index))
  }

  const handleNextStepsChange = (index, value) => {
    const steps = [...(formData.next_steps || [])]
    steps[index] = value
    updateFormData({ ...formData, next_steps: steps })
  }

  const addNextStep = () => {
    updateFormData({
      ...formData,
      next_steps: [...(formData.next_steps || []), '']
    })
  }

  const removeNextStep = (index) => {
    updateFormData({
      ...formData,
      next_steps: formData.next_steps.filter((_, i) => i !== index)
    })
  }

  const handlePillarChange = (pillarKey, updatedPillarData) => {
    updateFormData({
      ...formData,
      [pillarKey]: updatedPillarData
    })
  }

  const handleRemovePillar = (pillarKey) => {
    const { [pillarKey]: removed, ...rest } = formData
    updateFormData(rest)
  }

  const handleAIRefine = async () => {
    if (!prompt.trim()) {
      message.error('Please enter a refinement prompt')
      return
    }

    if (!clientId || !assessmentId) {
      message.error('Missing client or assessment ID for AI refinement')
      return
    }

    // Sync latest formData before AI refinement
    if (onChange) {
      onChange(formData)
    }

    setLoading(true)
    try {
      const response = await refineInfographic(
        clientId,
        assessmentId,
        'executive_summary',
        formData,
        prompt
      )

      const refinedContent = response.refined_content || ''
      
      try {
        const refinedParsed = typeof refinedContent === 'string' ? JSON.parse(refinedContent) : refinedContent
        setOriginalData(formData)
        setRefinedData(refinedParsed)
        setShowRefinedReview(true)
      } catch (e) {
        message.error('AI returned invalid format. Please try again.')
      }
      
    } catch (error) {
      message.error('Failed to refine executive summary. Please try again.')
      console.error('Error refining:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptRefined = () => {
    if (refinedData) {
      updateFormData(refinedData)
      message.success('Executive summary refined successfully!')
    }
    setShowRefinedReview(false)
    setRefinedData(null)
    setOriginalData(null)
    setPrompt('')
  }

  const handleDeclineRefined = () => {
    setShowRefinedReview(false)
    setRefinedData(null)
    setOriginalData(null)
    setPrompt('')
    message.info('Refinement declined')
  }

  return (
    <div style={{
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      border: '2px dashed #264382',
      position: 'relative'
    }}>
      {/* Top Right Buttons */}
      <div className={styles.editorTopButtons}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button type="primary" onClick={onSave} loading={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Show refinement review if active */}
      {showRefinedReview && originalData && refinedData ? (
        <RefinementReview
          originalData={originalData}
          refinedData={refinedData}
          onAccept={handleAcceptRefined}
          onDecline={handleDeclineRefined}
          title="Executive Summary"
        />
      ) : (
        <>
          {/* Overall Assessment Section */}
          <div className={styles.editorSection}>
            <h3>Overall Assessment</h3>
            <div className={styles.editorRow}>
              <label>Headline:</label>
              <Input
                value={(formData.overall_assessment && formData.overall_assessment.headline) || ''}
                onChange={e => handleOverallChange('headline', e.target.value)}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Current Score:</label>
              <InputNumber
                value={(formData.overall_assessment && formData.overall_assessment.current)}
                onChange={val => handleOverallChange('current', val)}
                min={0}
                max={100}
                style={{ width: '100%' }}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Desired Score (Target):</label>
              <InputNumber
                value={(formData.overall_assessment && formData.overall_assessment.desired)}
                onChange={val => handleOverallChange('desired', val)}
                min={0}
                max={100}
                style={{ width: '100%' }}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Trend:</label>
              <Select
                value={(formData.overall_assessment && formData.overall_assessment.trend) || 'stable'}
                options={trendOptions}
                onChange={val => handleOverallChange('trend', val)}
                style={{ width: '100%' }}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Description:</label>
              <TextArea
                value={(formData.overall_assessment && formData.overall_assessment.description) || ''}
                onChange={e => handleOverallChange('description', e.target.value)}
                rows={3}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Quick Actions:</label>
              <ListEditor
                items={(formData.overall_assessment && formData.overall_assessment.quick_actions) || []}
                onChange={handleOverallQuickActionsChange}
                onAdd={addOverallQuickAction}
                onRemove={removeOverallQuickAction}
                placeholder="Enter quick action"
                addButtonLabel="+ Add Quick Action"
              />
            </div>
          </div>

          {/* Key Metrics Section */}
          <div className={styles.editorSection}>
            <h3>Key Metrics to Track</h3>
            {(formData.key_metrics_to_track || []).map((metric, index) => (
              <div key={index} className={styles.metricEditor}>
                <div className={styles.editorRow}>
                  <label>Metric Name:</label>
                  <Input
                    value={metric.name}
                    onChange={e => handleMetricChange(index, 'name', e.target.value)}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Current Value:</label>
                  <InputNumber
                    value={metric.current_value}
                    onChange={val => handleMetricChange(index, 'current_value', val)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Target Value:</label>
                  <InputNumber
                    value={metric.target_value}
                    onChange={val => handleMetricChange(index, 'target_value', val)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Unit:</label>
                  <Input
                    value={metric.unit}
                    onChange={e => handleMetricChange(index, 'unit', e.target.value)}
                  />
                </div>
                <Button
                  danger
                  onClick={() => removeMetric(index)}
                  style={{ marginTop: '12px' }}
                >
                  Remove Metric
                </Button>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={addMetric}
              style={{ width: '100%', marginBottom: '16px' }}
            >
              + Add Metric
            </Button>
          </div>

          {/* Weekly Structure Section */}
          <div className={styles.editorSection}>
            <h3>Weekly Priorities & Focus</h3>
            
            {/* Weekly Grid Stats */}
            <div className={styles.weeklyGrid}>
              <div className={styles.weeklyCard} style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                <div className={styles.editorRow}>
                  <label>Current Exercise Days:</label>
                  <InputNumber
                    value={(formData.weekly_structure && formData.weekly_structure.current && formData.weekly_structure.current.exercise_days) || 0}
                    onChange={val => handleWeeklyCurrentChange('exercise_days', val)}
                    min={0}
                    max={7}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className={styles.weeklyCard} style={{ backgroundColor: '#e6f4ff', border: '1px solid #91caff' }}>
                <div className={styles.editorRow}>
                  <label>Target Exercise Days:</label>
                  <InputNumber
                    value={(formData.weekly_structure && formData.weekly_structure.target && formData.weekly_structure.target.exercise_days) || 0}
                    onChange={val => handleWeeklyTargetChange('exercise_days', val)}
                    min={0}
                    max={7}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className={styles.weeklyCard} style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
                <div className={styles.editorRow}>
                  <label>Current Rest Days:</label>
                  <InputNumber
                    value={(formData.weekly_structure && formData.weekly_structure.current && formData.weekly_structure.current.rest_days) || 0}
                    onChange={val => handleWeeklyCurrentChange('rest_days', val)}
                    min={0}
                    max={7}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className={styles.weeklyCard} style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
                <div className={styles.editorRow}>
                  <label>Target Rest Days:</label>
                  <InputNumber
                    value={(formData.weekly_structure && formData.weekly_structure.target && formData.weekly_structure.target.rest_days) || 0}
                    onChange={val => handleWeeklyTargetChange('rest_days', val)}
                    min={0}
                    max={7}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>

            {/* Focus Text Fields */}
            <div className={styles.editorRow}>
              <label>Where I Stand (Current):</label>
              <TextArea
                value={(formData.weekly_structure && formData.weekly_structure.where_i_stand) || ''}
                onChange={e => handleWeeklyChange('where_i_stand', e.target.value)}
                rows={3}
              />
            </div>
            <div className={styles.editorRow}>
              <label>What to Focus On (Desired):</label>
              <TextArea
                value={(formData.weekly_structure && formData.weekly_structure.what_to_focus_on) || ''}
                onChange={e => handleWeeklyChange('what_to_focus_on', e.target.value)}
                rows={3}
              />
            </div>

            {/* Action Items List */}
            <div className={styles.editorRow}>
              <label>What to Do (Action Items):</label>
              <ListEditor
                items={formData.weekly_structure.what_to_do || []}
                onChange={handleWeeklyActionsChange}
                onAdd={addWeeklyAction}
                onRemove={removeWeeklyAction}
                placeholder="Enter action item"
                addButtonLabel="+ Add Action Item"
              />
            </div>
          </div>

          {/* Exercise Pillar Section */}
          {(formData.exercise && formData.exercise.headline) && (
            <div className={styles.editorSection}>
              <h3>Exercise Pillar</h3>
              <PillarFields
                data={formData.exercise}
                onChange={(updated) => handlePillarChange('exercise', updated)}
                onRemove={() => handleRemovePillar('exercise')}
              />
            </div>
          )}

          {/* Nutrition Pillar Section */}
          {(formData.nutrition && formData.nutrition.headline) && (
            <div className={styles.editorSection}>
              <h3>Nutrition Pillar</h3>
              <PillarFields
                data={formData.nutrition}
                onChange={(updated) => handlePillarChange('nutrition', updated)}
                onRemove={() => handleRemovePillar('nutrition')}
              />
            </div>
          )}

          {/* Sleep Pillar Section */}
          {(formData.sleep && formData.sleep.headline) && (
            <div className={styles.editorSection}>
              <h3>Sleep Pillar</h3>
              <PillarFields
                data={formData.sleep}
                onChange={(updated) => handlePillarChange('sleep', updated)}
                onRemove={() => handleRemovePillar('sleep')}
              />
            </div>
          )}

          {/* Next Steps Section */}
          <div className={styles.editorSection}>
            <h3>Next Steps</h3>
            <ListEditor
              items={formData.next_steps || []}
              onChange={handleNextStepsChange}
              onAdd={addNextStep}
              onRemove={removeNextStep}
              placeholder="Enter action step"
              addButtonLabel="+ Add Next Step"
            />
          </div>

          {/* AI Refine Section */}
          <div className={styles.editorAIRefine}>
            <div className={styles.editorAIRefineTitle}>
              <EditOutlined style={{ marginRight: '8px' }} />
              Refine with AI
            </div>
            <TextArea
              placeholder="Enter refinement instructions (e.g., 'Make the headline more impactful', 'Focus on exercise improvements')"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={2}
              style={{ marginBottom: '8px' }}
            />
            <Button
              type="primary"
              onClick={handleAIRefine}
              loading={loading}
              disabled={!prompt.trim()}
              icon={<EditOutlined />}
            >
              {loading ? 'Refining...' : 'Refine with AI'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

const PillarFields = ({ data, onChange, onRemove }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value })
  }

  const handleQuickActionChange = (index, value) => {
    const actions = [...(data.quick_actions || [])]
    actions[index] = value
    handleChange('quick_actions', actions)
  }

  const addQuickAction = () => {
    handleChange('quick_actions', [...(data.quick_actions || []), ''])
  }

  const removeQuickAction = (index) => {
    handleChange('quick_actions', data.quick_actions.filter((_, i) => i !== index))
  }

  return (
    <>
      <div className={styles.editorRow}>
        <label>Headline:</label>
        <Input
          value={data.headline}
          onChange={e => handleChange('headline', e.target.value)}
        />
      </div>
      <div className={styles.editorRow}>
        <label>Score:</label>
        <InputNumber
          value={data.score}
          onChange={val => handleChange('score', val)}
          min={0}
          max={100}
          style={{ width: '100%' }}
        />
      </div>
      <div className={styles.editorRow}>
        <label>Target Score:</label>
        <InputNumber
          value={data.target_score}
          onChange={val => handleChange('target_score', val)}
          min={0}
          max={100}
          style={{ width: '100%' }}
        />
      </div>
      <div className={styles.editorRow}>
        <label>Priority:</label>
        <Select
          value={data.priority}
          options={priorityOptions}
          onChange={val => handleChange('priority', val)}
          style={{ width: '100%' }}
        />
      </div>
      <div className={styles.editorRow}>
        <label>Current Status:</label>
        <TextArea
          value={data.current_status}
          onChange={e => handleChange('current_status', e.target.value)}
          rows={3}
        />
      </div>
      <div className={styles.editorRow}>
        <label>Quick Actions:</label>
        <ListEditor
          items={data.quick_actions || []}
          onChange={handleQuickActionChange}
          onAdd={addQuickAction}
          onRemove={removeQuickAction}
          placeholder="Enter quick action"
          addButtonLabel="+ Add Quick Action"
        />
      </div>
      <Button
        danger
        onClick={onRemove}
        style={{ marginTop: '12px' }}
      >
        Remove Pillar
      </Button>
    </>
  )
}

export default ExecutiveSummaryEditor